//** Import Components */
import { useState } from "react";
import axiosWithAuth from "../utils/axiosWithAuth";
import {
  Form,
  Input,
  Modal,
  Button,
  message,
  Table,
  Tooltip,
  Empty,
  Space,
  Collapse,
  Grid,
  Pagination,
  Tag,
} from "antd";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import { TABLE_DATE_FORMAT } from "../common/constants";
import { FE_URL, BE_API_URL } from "../common/constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt, faEdit } from "@fortawesome/free-solid-svg-icons";
import { CheckCircleOutlined, SyncOutlined } from "@ant-design/icons";

export const useManagePromoteVideos = ({
  itemType,
  modalData,
  dispatchAction,
  deleteItemAPI,
  business,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [modalForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const { Panel } = Collapse;

  //* Function to create new item
  const createListItem = (values) => {
    const APIURL = `/api/business/mediaBusiness`;

    const listingData = {
      business_id: business.id,
      url: values.url,
      name: values.name,
      titleMedia: values.titleMedia,
      FEIBusiness:
        business.FEIBusiness || business.feiBusiness || business.fei_business,
    };

    axiosWithAuth()
      .post(APIURL, listingData)
      .then((res) => {
        console.log(res.data);

        let config = {
          content: `New ${itemType} created!`,
          style: {
            background: "none",
          },
        };
        message.success(config);

        closeModal();
        setIsSubmitting(false);
        dispatch(dispatchAction);
        modalForm.resetFields();
      })
      .catch((err) => {
        console.log(err.response);
        message.error(err.message);
        setIsSubmitting(false);
      });
  };

  //* Function to edit video name
  const editVideoName = (values) => {
    const APIURL = `/api/business/mediaBusiness/changeName`;

    const editData = {
      name: values.name,
      media_id: editingVideo.id,
    };

    axiosWithAuth()
      .put(APIURL, editData)
      .then((res) => {
        console.log(res.data);
        message.success("Video name updated successfully!");
        closeEditModal();
        setIsEditing(false);
        dispatch(dispatchAction);
        editForm.resetFields();
      })
      .catch((err) => {
        console.log(err.response);
        message.error(err.message || "Error updating video name");
        setIsEditing(false);
      });
  };

  //* Function to delete a list item
  const deleteListItem = (APIURL) => {
    if (window.confirm("Are you sure you wish to delete this item?")) {
      axiosWithAuth()
        .delete(APIURL)
        .then((res) => {
          dispatch(dispatchAction);
          console.log(res.data, APIURL);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  };

  //* functions to handle modal
  const showModal = () => {
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
  };

  const submitModalForm = () => {
    setIsSubmitting(true);
    modalForm.submit();
  };

  //* functions to handle edit modal
  const showEditModal = (video) => {
    setEditingVideo(video);
    editForm.setFieldsValue({ name: video.name });
    setEditModal(true);
  };

  const closeEditModal = () => {
    setEditModal(false);
    setEditingVideo(null);
  };

  const submitEditForm = () => {
    setIsEditing(true);
    editForm.submit();
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
    setIsSubmitting(false);
  };

  const onEditFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
    setIsEditing(false);
  };

  //* Component that holds the modal
  const modalComponent = (
    <Modal
      open={openModal}
      title={modalData.title}
      onCancel={closeModal}
      footer={[
        <Button key="back" onClick={closeModal}>
          Close
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isSubmitting}
          onClick={submitModalForm}
        >
          Submit
        </Button>,
      ]}
    >
      <Form
        form={modalForm}
        layout="vertical"
        name="userForm"
        onFinish={createListItem}
        onFinishFailed={onFinishFailed}
      >
        <Form.Item
          name="name"
          label={modalData.fieldLabel}
          rules={[
            {
              required: true,
              message: "Please input a name!",
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="titleMedia"
          label="Video Title"
          rules={[
            {
              required: true,
              message: "Please input a video title!",
            },
          ]}
        >
          <Input placeholder="Enter video title" />
        </Form.Item>
        <Form.Item
          name="url"
          label="Video URL"
          rules={[
            {
              required: true,
              message: "Please input an url!",
            },
            {
              type: "url",
              message: "Please enter a valid URL!",
            },
            {
              pattern: /^https?:\/\/.+/,
              message: "URL must start with http:// or https://",
            },
          ]}
        >
          <Input placeholder="https://example.com" />
        </Form.Item>
      </Form>
    </Modal>
  );

  //* Component that holds the edit modal
  const editModalComponent = (
    <Modal
      open={editModal}
      title="Edit Video Name"
      onCancel={closeEditModal}
      footer={[
        <Button key="back" onClick={closeEditModal}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isEditing}
          onClick={submitEditForm}
        >
          Update
        </Button>,
      ]}
    >
      <Form
        form={editForm}
        layout="vertical"
        name="editForm"
        onFinish={editVideoName}
        onFinishFailed={onEditFinishFailed}
      >
        <Form.Item
          name="name"
          label="Video Name"
          rules={[
            {
              required: true,
              message: "Please input a name!",
            },
          ]}
        >
          <Input placeholder="Enter video name" />
        </Form.Item>
      </Form>
    </Modal>
  );

  const CreateItemButton = ({ btnText }) => {
    return (
      <div className="create-item">
        <Button type="primary" onClick={showModal}>
          {btnText}
        </Button>
      </div>
    );
  };

  const dateFormat = TABLE_DATE_FORMAT;

  const columns = [
    {
      title: "Name",
      key: "name",
      render: (text) => <p>{text.name}</p>,
    },
    {
      title: "URL",
      key: "url",
      render: (text) => <p>{text.url}</p>,
    },
    {
      title: "Status",
      key: "status",
      render: (text) => (
        <>
          {text.approved === 0 && (
            <Tag icon={<SyncOutlined spin />} color="processing">
              Pending
            </Tag>
          )}
          {text.approved === 1 && (
            <Tag icon={<CheckCircleOutlined />} color="success">
              Approved
            </Tag>
          )}
        </>
      ),
    },
    {
      title: "Date Created",
      key: "created",
      render: (text) => (
        <>{moment(new Date(text && text.created_at)).format(dateFormat)}</>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (text) => (
        <Space size="middle">
          <button
            onClick={() => {
              showEditModal(text);
            }}
          >
            <Tooltip title="Edit">
              <FontAwesomeIcon icon={faEdit} />
            </Tooltip>
          </button>
          <button
            onClick={() => {
              const APIURL = `${deleteItemAPI}/${text.id}`;
              deleteListItem(APIURL, text.id);
            }}
          >
            <Tooltip title="Delete">
              <FontAwesomeIcon icon={faTrashAlt} />
            </Tooltip>
          </button>
        </Space>
      ),
    },
  ];

  const CardListComponent = ({ listItems }) => {
    // Ensure listItems is always an array
    const safeListItems = Array.isArray(listItems) ? listItems : [];
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    const start = (currentPage - 1) * pageSize;
    const currentItems = safeListItems.slice(start, start + pageSize);

    return (
      <>
        <Collapse accordion>
          {currentItems.map((item) => (
            <Panel
              key={item.id}
              header={
                <div>
                  <strong>{item.name}</strong>
                  <div style={{ float: "right" }}>
                    {item.approved === 0 && (
                      <Tag icon={<SyncOutlined spin />} color="processing">
                        Pending
                      </Tag>
                    )}
                    {item.approved === 1 && (
                      <Tag icon={<CheckCircleOutlined />} color="success">
                        Approved
                      </Tag>
                    )}
                  </div>
                </div>
              }
            >
              <p>
                <strong>Name:</strong> {item.name}
              </p>
              <p>
                <strong>URL:</strong> {item.url}
              </p>
              <Space size="middle">
                <Button
                  onClick={() => {
                    showEditModal(item);
                  }}
                >
                  <Tooltip title="Edit">
                    <FontAwesomeIcon icon={faEdit} />
                  </Tooltip>
                </Button>
                <Button
                  onClick={() => {
                    const APIURL = `${deleteItemAPI}/${item.id}`;
                    deleteListItem(APIURL, item.id);
                  }}
                >
                  <Tooltip title="Delete">
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </Tooltip>
                </Button>
              </Space>
            </Panel>
          ))}
        </Collapse>

        <div style={{ marginTop: 16, textAlign: "center" }}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={safeListItems.length}
            onChange={(page) => setCurrentPage(page)}
            size="small"
          />
        </div>
      </>
    );
  };

  const ListComponent = ({ emptyMessage, listItems }) => {
    // Ensure listItems is always an array
    const safeListItems = Array.isArray(listItems) ? listItems : [];

    if (safeListItems.length === 0) {
      return (
        <Empty
          image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
          imageStyle={{ height: 60 }}
          description={<span>{emptyMessage}</span>}
        />
      );
    }

    return screens.md ? (
      <Table columns={columns} dataSource={safeListItems} />
    ) : (
      <CardListComponent listItems={safeListItems} />
    );
  };

  return [modalComponent, editModalComponent, CreateItemButton, ListComponent];
};
